/**
 * Funciones de validación para navigation guards
 * Extraídas para facilitar testing sin dependencias del store
 */

/**
 * Verifica si el usuario actual es un distribuidor avanzado
 * @param {Object} userData - Datos del usuario
 * @returns {boolean}
 */
export function isDistribuidorAvanzado(userData) {
	return !!(userData &&
		userData.distributor_code &&
		userData.available_features &&
		userData.available_features.feature_distribuidor_avanzado);
}

/**
 * Verifica si el usuario actual es un trabajador de distribuidor avanzado
 * @param {Object} userData - Datos del usuario
 * @returns {boolean}
 */
export function isTrabajadorDistribuidorAvanzado(userData) {
	return !!(userData &&
		userData.sender_docs_user_id &&
		userData.available_features &&
		userData.available_features.feature_trabajador_distribuidor_avanzado);
}

/**
 * Verifica si el usuario de sesión (loginas) es un distribuidor avanzado
 * @param {Object} userData - Datos del usuario
 * @returns {boolean}
 */
export function isSessionUserDistribuidorAvanzado(userData) {
	return !!(userData &&
		userData.session_user &&
		userData.session_user.is_distribuidor_avanzado);
}

/**
 * Verifica si el usuario de sesión (loginas) es un trabajador de distribuidor avanzado
 * @param {Object} userData - Datos del usuario
 * @returns {boolean}
 */
export function isSessionUserTrabajadorDistribuidorAvanzado(userData) {
	return !!(userData &&
		userData.session_user &&
		userData.session_user.is_trabajador_distribuidor_avanzado);
}

/**
 * Verifica si el usuario puede acceder a /debug_distribuidor
 * Casos permitidos:
 * 1. Usuario actual es distribuidor avanzado (distributor_code + feature_distribuidor_avanzado)
 * 2. Usuario actual es trabajador de distribuidor avanzado (sender_docs_user_id + feature_trabajador_distribuidor_avanzado)
 * 3. Usuario de sesión (loginas) es distribuidor avanzado
 * 4. Usuario de sesión (loginas) es trabajador de distribuidor avanzado
 * @param {Object} userData - Datos del usuario
 * @returns {boolean}
 */
export function canAccessDebugDistribuidor(userData) {
	return isDistribuidorAvanzado(userData) ||
		isTrabajadorDistribuidorAvanzado(userData) ||
		isSessionUserDistribuidorAvanzado(userData) ||
		isSessionUserTrabajadorDistribuidorAvanzado(userData);
}

export function isFichajesOnly(userData) {
	// Why: usuarios creados desde la pantalla de fichajes (BulkCreateEmployees y popup
	// individual) tienen friendship_kind='employee', view_working_hours=True y todos los
	// demás view_* a False. Para estos usuarios el sidebar, top-bar y rutas deben
	// restringirse a fichajes. Es CRÍTICO exigir también que sea empleado: clientes,
	// admins u owners pueden tener únicamente view_working_hours por configuración y NO
	// queremos forzarles a entrar en /working-hours al inicio de sesión — eso los
	// llevaría a la pantalla de prueba gratuíta cuando la company elegida no tiene
	// contrato propio. Solo el empleado entra de primeras al módulo de fichajes.
	if (!userData) return false;
	const roleValue = userData.user_role && userData.user_role.value;
	if (roleValue !== 'employee') return false;
	return !!(
		userData.can_view_working_hours &&
		!userData.can_view_invoices &&
		!userData.can_view_banks &&
		!userData.can_view_e_invoice &&
		!userData.can_view_gestor
	);
}

export function isWorkingHoursPath(path) {
	// Why: 'working-hours-trial' contiene 'working-hours' como substring; si lo
	// consideramos ruta válida, el guard de fichajes-only no redirige fuera del
	// trial y un usuario con contrato activo se queda atrapado en esa pantalla.
	return typeof path === 'string'
		&& path.includes('working-hours')
		&& !path.includes('working-hours-trial');
}

export async function resolveAlbaranAccessRedirect(route, loadAccess) {
	const albaranAccessConfig = route.meta && route.meta.albaranAccess;
	if (!albaranAccessConfig) return null;

	const companyId = Number(route.params.companydb_id);
	const deniedRedirect = albaranAccessConfig.deniedRedirect
		|| `/${companyId}/albaran-factura-list?tab=albaranes`;

	try {
		const access = await loadAccess(companyId);
		const isCreation = albaranAccessConfig.createAction
			&& albaranAccessConfig.resourceParam
			&& !route.params[albaranAccessConfig.resourceParam];
		const requiredAction = isCreation
			? albaranAccessConfig.createAction
			: albaranAccessConfig.viewAction;
		const isActionAllowed = access.allowed_actions.includes(requiredAction);
		const isCatalogWriteAllowed = !albaranAccessConfig.isCatalogWriteRequired
			|| access.catalog_read_only === false;
		// Pedidos open only while a flow of the company works with them: the permission alone does not open them.
		const isPedidosAllowed = !albaranAccessConfig.isPedidosRequired
			|| access.works_with_pedidos === true;

		return isActionAllowed && isCatalogWriteAllowed && isPedidosAllowed ? null : deniedRedirect;
	} catch (error) {
		return deniedRedirect;
	}
}

/**
 * A dónde mandar a quien pide una pantalla de nóminas, o `null` para dejarle pasar.
 *
 * Dos comprobaciones, y el orden importa: primero el PERMISO y después el CONECTOR. A
 * quien no tiene permiso no se le explica un módulo que no va a poder usar; a quien sí lo
 * tiene pero su programa contable no está soportado se le lleva a una vista que le cuenta
 * de qué va y para qué conectores está (`featureRedirect`), en vez de dejarle en otra
 * pantalla sin pista de qué ha pasado.
 *
 * `hasFeature` se recibe de fuera en lugar de leer el store aquí: mantiene el guard
 * probable sin montar Vuex, igual que `loadAccess`.
 */
export async function resolveNominaAccessRedirect(route, loadAccess, hasFeature) {
	const nominaAccessConfig = route.meta && route.meta.nominaAccess;
	if (!nominaAccessConfig) return null;

	const companyId = Number(route.params.companydb_id);
	const deniedRedirect = nominaAccessConfig.deniedRedirect || '/initial';

	try {
		const access = await loadAccess(companyId);
		const isActionAllowed = access.allowed_actions.includes(nominaAccessConfig.viewAction);
		if (!isActionAllowed) return deniedRedirect;
		// Solo las rutas que declaran a dónde ir: las demás (papelera, detalle) se
		// alcanzan desde el listado, que ya ha hecho esta comprobación.
		if (nominaAccessConfig.featureRedirect && hasFeature && !hasFeature('feature_nominas')) {
			return `/${companyId}${nominaAccessConfig.featureRedirect}`;
		}
		return null;
	} catch (error) {
		return deniedRedirect;
	}
}

export async function resolveInvoiceApprovalAccessRedirect(to, loadAccess) {
	const config = to.meta && to.meta.invoiceApprovalAccess;
	if (!config) return null;
	const companyId = Number(to.params.companydb_id);
	if (!Number.isSafeInteger(companyId) || companyId <= 0) return '/initial';
	try {
		const access = await loadAccess(companyId);
		return access && access.enabled && Array.isArray(access.allowed_actions)
			&& access.allowed_actions.includes(config.viewAction) ? null : '/initial';
	} catch (error) {
		return '/initial';
	}
}
